import { DatePicker, Drawer, message, Select } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import { createTax, fetchAllTaxes, updateTax } from "../../../../core/data/redux/payroll/TDS/taxes/taxActions";
import { status } from "../../../../core/common/selectoption/selectoption";
import dayjs from "dayjs";
import { fetchTdsTaxRegimes } from "../../../../core/data/redux/payroll/TDS/tdsTaxRegime/tdsTaxRegimeActions";

export interface TaxFormData {
    tax_name: number | "";
    tax_percentage: string;
    description: string;
    status: string;
    createdBy: number;
    effective_date: dayjs.Dayjs | null;
    min_amount: string;
    max_amount: string;
    age: string;
    gender: string;
}

const TaxModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loadingStates } = useSelector((state: any) => state.accounting.taxes); // adjust state slice name
    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;
    const { regimes = [] } = useSelector((state: any) => state.TDS.tdsTaxRegime);

    const [formData, setFormData] = useState<TaxFormData>({
        tax_name: "",
        tax_percentage: "",
        description: "",
        status: "active",
        createdBy: user?.UserId || 1,
        effective_date: null,   // new
        min_amount: "",         // new
        max_amount: "",
        age: "",
        gender: "",
    });
    const [errors, setErrors] = useState<any>({});
    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                tax_name: selectedRowData.tax_name ? Number(selectedRowData.tax_name) : "",
                tax_percentage: selectedRowData.tax_percentage?.toString() || "",
                description: selectedRowData.description || "",
                status: selectedRowData.status || "active",
                createdBy: user?.UserId || 1,
                effective_date: selectedRowData.effective_date ? dayjs(selectedRowData.effective_date) : null,
                min_amount: selectedRowData.min_amount?.toString() || "",
                max_amount: selectedRowData.max_amount === 0 || selectedRowData.max_amount == null ? "" : selectedRowData.max_amount.toString(),
                age: selectedRowData.age != null ? String(selectedRowData.age) : "",
                gender: selectedRowData.gender || "",
            });
            setErrors({});
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData]);


    useEffect(() => {
        if (!regimes.length) {
            dispatch(fetchTdsTaxRegimes());
        }
    }, [dispatch, regimes.length]);

    const resetForm = () => {
        setFormData({
            tax_name: "",
            tax_percentage: "",
            description: "",
            status: "active",
            createdBy: user?.UserId || 1,
            effective_date: null,
            min_amount: "",
            max_amount: "",
            age: "",
            gender: "",
        });
        setErrors({});
    };

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const normalizeAgeRange = (s: string) =>
        s
            // convert many dash-like chars to a simple hyphen
            .replace(/[–—−‐-\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-")
            // collapse multiple spaces
            .replace(/\s+/g, " ")
            .trim();
    const parseAgeRange = (raw: string) => {
        const s = normalizeAgeRange(raw);
        const m = s.match(/^(\d{1,3})\s*-\s*(\d{1,3})$/);
        if (!m) return { ok: false as const, from: NaN, to: NaN, formatted: s };
        const from = parseInt(m[1], 10);
        const to = parseInt(m[2], 10);
        return { ok: true as const, from, to, formatted: `${from} - ${to}` };
    };


    const validateForm = () => {
        let isValid = true;
        const newErrors: any = {};

        if (formData.tax_name === "" || Number.isNaN(Number(formData.tax_name))) {
            newErrors.tax_name = "Regime is required.";
            isValid = false;
        }

        const min = parseFloat(formData.min_amount);
        const max = parseFloat(formData.max_amount);
        if (isNaN(min) || min < 0) {
            newErrors.min_amount = "Valid minimum amount is required.";
            isValid = false;
        }

        // if (isNaN(max)) {
        //     newErrors.max_amount = "Valid maximum amount is required.";
        //     isValid = false;
        // }

        // if (!isNaN(min) && !isNaN(max) && min > max) {
        //     newErrors.min_amount = "Minimum amount cannot be greater than maximum amount.";
        //     isValid = false;
        // }

        if (!formData.effective_date) {
            newErrors.effective_date = "Effective date is required.";
            isValid = false;
        }

        const percentage = parseFloat(formData.tax_percentage);
        if (!formData.tax_percentage || isNaN(percentage)) {
            newErrors.tax_percentage = "Valid tax percentage is required.";
            isValid = false;
        } else if (percentage < 0 || percentage > 100) {
            newErrors.tax_percentage = "Tax percentage must be between 0 and 100.";
            isValid = false;
        }

        if (!formData.status) {
            newErrors.status = "Status is required.";
            isValid = false;
        }
        if (!formData.age) {
            newErrors.age = "Age range is required.";
            isValid = false;
        } else {
            const { ok, from, to } = parseAgeRange(formData.age);

            if (!ok) {
                newErrors.age = "Enter a valid range like 10 - 20.";
                isValid = false;
            } else if (from < 0 || to < 0 || from > 120 || to > 120) {
                newErrors.age = "Ages must be between 0 and 120.";
                isValid = false;
            } else if (from > to) {
                newErrors.age = "From age cannot be greater than To age.";
                isValid = false;
            } else if (from < 18 || to < 18) {
                newErrors.age = "Age must be at least 18 years old.";
                isValid = false;
            }
        }

        if (!formData.gender) {
            newErrors.gender = "Gender is required.";
            isValid = false;
        } else if (!["Male", "Female", , "Both", "Others"].includes(formData.gender)) {
            newErrors.gender = "Gender must be Male, Female,Both or Others.";
            isValid = false;
        }


        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const payload = {
            ...formData,
            tax_name: formData.tax_name === "" ? null : Number(formData.tax_name),
            tax_percentage: parseFloat(formData.tax_percentage),
            effective_date: formData.effective_date?.format("YYYY-MM-DD") || null,
            min_amount: parseFloat(formData.min_amount) || 0,
            max_amount: parseFloat(formData.max_amount) || 0,
            age: formData.age === "" ? null : formData.age.trim(),
            gender: formData.gender || null,
        };

        try {
            if (modalType === "edit") {
                const updatedPayload = {
                    ...payload,
                    modifiedBy: user?.UserId || 1,
                };
                await dispatch(updateTax({ id: selectedRowData.tax_id, data: updatedPayload })).unwrap();
                message.success("Tax updated successfully");
            } else {
                await dispatch(createTax(payload)).unwrap();
                message.success("Tax created successfully");
            }
            handleCancel();
            resetForm();
            await dispatch(fetchAllTaxes());
        } catch (err) {
            message.error("Something went wrong. Please try again.");
        }
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit TDS Slab" : "Add TDS Slab"}
            placement="right"
            width={600}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
            style={{ marginTop: 50, paddingBottom: 50 }}
        >
            <form>
                <div className="modal-body pb-0">
                    <div className="row">
                        <div className="mb-3 col-md-6">
                            <label className="form-label">
                                Select Regime <span className="text-danger"> *</span>
                            </label>
                            <Select
                                showSearch
                                placeholder="Choose a regime"
                                className="w-100"
                                value={formData.tax_name === "" ? undefined : formData.tax_name}
                                onChange={(val) => handleChange("tax_name", val)}
                                optionFilterProp="label"
                                status={errors.tax_name ? "error" : ""}
                                filterOption={(input, option) =>
                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {regimes
                                    .filter((r: any) => r.Status === "active")
                                    .map((r: any) => (
                                        <Select.Option
                                            key={r.TaxRegimeID}
                                            value={Number(r.TaxRegimeID)}
                                            label={`${r.RegimeName} (${r.FinancialYearName || "-"})`}
                                        >
                                            <div className="d-flex flex-column">
                                                <small className="fw-500">{r.RegimeName} - {r.FinancialYearName || "-"} {Number(r.IsDefault) === 1 ? " • Default" : ""}</small>
                                            </div>
                                        </Select.Option>
                                    ))}
                            </Select>
                            {errors.tax_name && <div className="text-danger mt-1">{errors.tax_name}</div>}
                        </div>

                        <div className="mb-3  col-md-6">
                            <label className="form-label">Effective Date <span className="text-danger"> *</span></label>
                            <div className="position-relative">
                                <DatePicker
                                    value={formData.effective_date}
                                    onChange={(date) => handleChange("effective_date", date)}
                                    className={`w-100 ${errors.effective_date ? "is-invalid" : ""}`}
                                    //  className={`form-control ${errors.age ? "is-invalid" : ""}`}
                                    format="YYYY-MM-DD"
                                />
                                <span className="input-icon-addon">
                                    <i className="ti ti-calendar text-gray-7" />
                                </span>

                            </div>
                            {errors.effective_date && <div className="text-danger mt-1">{errors.effective_date}</div>}

                        </div>
                    </div>

                    <div className="row">
                        <div className="mb-3  col-md-6">
                            <label className="form-label">Age <span className="text-danger"> *</span></label>
                            <input
                                type="text"
                                className={`form-control ${errors.age ? "is-invalid" : ""}`}
                                value={formData.age}
                                onChange={(e) => handleChange("age", e.target.value)}
                                min={0}
                                max={120}
                                step={1}
                            />
                            {errors.age && <div className="invalid-feedback">{errors.age}</div>}
                        </div>

                        <div className="mb-3  col-md-6">
                            <label className="form-label">Gender <span className="text-danger"> *</span></label>
                            <Select
                                value={formData.gender}
                                onChange={(value) => handleChange("gender", value)}
                                className={`w-100 ${errors.gender ? "is-invalid-select" : ""}`}
                                placeholder="Select gender"
                            >
                                <Select.Option value="Male">Male</Select.Option>
                                <Select.Option value="Female">Female</Select.Option>
                                <Select.Option value="Both">Both</Select.Option>
                                <Select.Option value="Others">Others</Select.Option>
                            </Select>
                            {errors.gender && <div className="text-danger mt-1">{errors.gender}</div>}
                        </div>
                    </div>

                    <div className="row">
                        <div className="mb-3  col-md-6">
                            <label className="form-label">Min Amount <span className="text-danger"> *</span></label>
                            <input
                                type="text"
                                className={`form-control ${errors.min_amount ? "is-invalid" : ""}`}
                                value={formData.min_amount}
                                onChange={(e) => handleChange("min_amount", e.target.value.replace(/[^0-9.]/g, ""))} // Allow only numbers and decimal point
                            />
                            {errors.min_amount && <div className="invalid-feedback">{errors.min_amount}</div>}
                        </div>

                        <div className="mb-3  col-md-6">
                            <label className="form-label">Max Amount </label>
                            <input
                                type="text"
                                className={`form-control ${errors.max_amount ? "is-invalid" : ""}`}
                                value={formData.max_amount}
                                onChange={(e) => handleChange("max_amount", e.target.value.replace(/[^0-9.]/g, ""))} // Allow only numbers and decimal point
                            />
                            {errors.max_amount && <div className="invalid-feedback">{errors.max_amount}</div>}
                        </div>
                    </div>
                    <div className="row">
                        <div className="mb-3  col-md-6">
                            <label className="form-label">Slab Tax Percentage (%) <span className="text-danger"> *</span></label>
                            <input
                                type="number"
                                step="0.01"
                                className={`form-control ${errors.tax_percentage ? "is-invalid" : ""}`}
                                value={formData.tax_percentage}
                                onChange={(e) => handleChange("tax_percentage", e.target.value)}
                            />
                            {errors.tax_percentage && <div className="invalid-feedback">{errors.tax_percentage}</div>}
                        </div>
                        <div className="mb-3  col-md-6">
                            <label className="form-label">Status <span className="text-danger"> *</span></label>
                            <Select
                                value={formData.status}
                                onChange={(value) => handleChange("status", value)}
                                className={`w-100 ${errors.status ? "is-invalid-select" : ""}`}
                            >
                                {status.map((option) => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                            {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control"
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </div>


                </div>
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-white border me-2"
                        onClick={() => {
                            handleCancel();
                            resetForm();
                        }}
                    >
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : null}
                        {modalType === "edit" ? "Update TDS Slab" : "Add TDS Slab"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default TaxModal;
